import React, { type ComponentProps, useMemo } from 'react';
import { StyleSheet } from 'react-native';
import Animated, { FadeIn } from 'react-native-reanimated';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';

import { ContentfulImage } from '../Image';

// ─────────────────────────────────────────────────────────────────────────────

export const BrandIcon = (props: Props) => {
  const { src, alt, size = DEFAULT_ICON_SIZE, width = size, height = size, style } = props;

  // ─── Styles ──────────────────────────────────────────────────────────

  const iconStyle = useStyle(() => ({ width, height }), [width, height]);

  // ─────────────────────────────────────────────────────────────────────

  const imageOptions = useMemo<ContentfulImageOptions>(
    () => ({ fm: 'webp', h: height * IMAGE_SCALE, w: width * IMAGE_SCALE }),
    [height, width],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Animated.View
      entering={FadeIn.duration(theme.transitions.short)}
      pointerEvents="none"
      style={[styles.container, iconStyle, style]}
    >
      <ContentfulImage
        baseUrl={src}
        alt={alt}
        contentFit="contain"
        options={imageOptions}
        style={[styles.image, iconStyle]}
      />
    </Animated.View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const DEFAULT_ICON_SIZE = 34;
const IMAGE_SCALE = 2;

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  image: {
    height: DEFAULT_ICON_SIZE,
    width: DEFAULT_ICON_SIZE,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  src: string;
  alt: string | undefined;
  size?: number;

  /**
   * Overrides the `size` width, for non-square icons.
   */
  width?: number;

  /**
   * Overrides the `size` height, for non-square icons.
   */
  height?: number;

  style?: ComponentProps<typeof Animated.View>['style'];
};

type ContentfulImageOptions = ComponentProps<typeof ContentfulImage>['options'];
